import { sanitizeHtml } from "../../sanitizeHtml";

export type CaretContentSplit = {
    position: "start" | "middle" | "end";
    before: string;
    after: string;
};

function getCollapsedCaret(): Range | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;
    return selection.getRangeAt(0);
}

function rangeToHtml(range: Range): string {
    const container = document.createElement("div");
    container.appendChild(range.cloneContents());
    return container.innerHTML;
}

function getContentAroundCaret(
    element: HTMLElement,
    mode: "text" | "html",
): { before: string; after: string } {
    const caret = getCollapsedCaret();
    if (!caret || !element.contains(caret.startContainer)) {
        const full = mode === "html" ? sanitizeHtml(element.innerHTML) : (element.textContent ?? "");
        return { before: full, after: "" };
    }

    const beforeRange = document.createRange();
    beforeRange.selectNodeContents(element);
    beforeRange.setEnd(caret.startContainer, caret.startOffset);

    const afterRange = document.createRange();
    afterRange.selectNodeContents(element);
    afterRange.setStart(caret.startContainer, caret.startOffset);

    if (mode === "text") {
        return { before: beforeRange.toString(), after: afterRange.toString() };
    }

    return {
        before: sanitizeHtml(rangeToHtml(beforeRange)),
        after: sanitizeHtml(rangeToHtml(afterRange)),
    };
}

export function getEnterCaretPosition(element: HTMLElement): "start" | "middle" | "end" {
    const caret = getCollapsedCaret();
    if (!caret || !element.contains(caret.startContainer)) return "end";
    if (isCaretAtEnd(element)) return "end";
    if (isCaretAtStart(element)) return "start";
    return "middle";
}

export function splitContentAtCaret(
    element: HTMLElement,
    mode: "text" | "html",
): CaretContentSplit {
    return {
        position: getEnterCaretPosition(element),
        ...getContentAroundCaret(element, mode),
    };
}

export function applyEditableContent(element: HTMLElement, content: string, mode: "text" | "html") {
    if (mode === "html") element.innerHTML = sanitizeHtml(content);
    else element.textContent = content;
}

export function isCaretAtStart(element: HTMLElement): boolean {
    const caret = getCollapsedCaret();
    if (!caret || !element.contains(caret.startContainer)) return false;
    const before = document.createRange();
    before.selectNodeContents(element);
    before.setEnd(caret.startContainer, caret.startOffset);
    return before.toString() === "";
}

export function isCaretAtEnd(element: HTMLElement): boolean {
    const caret = getCollapsedCaret();
    if (!caret || !element.contains(caret.startContainer)) return false;
    const after = document.createRange();
    after.selectNodeContents(element);
    after.setStart(caret.startContainer, caret.startOffset);
    return after.toString() === "";
}

export function focusAtStart(element: HTMLElement) {
    focusAtEdge(element, true);
}

export function focusAtEnd(element: HTMLElement) {
    focusAtEdge(element, false);
}

export function focusAtTextOffset(element: HTMLElement, offset: number) {
    element.focus();
    const selection = window.getSelection();
    if (!selection) return;
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let remaining = Math.max(0, offset);
    let lastText: Text | null = null;
    while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        lastText = node;
        if (remaining <= node.data.length) {
            const range = document.createRange();
            range.setStart(node, remaining);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            return;
        }
        remaining -= node.data.length;
    }
    if (lastText) {
        const range = document.createRange();
        range.setStart(lastText, lastText.data.length);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
    }
    focusAtEnd(element);
}

export function applyFocus(element: HTMLElement, at: "start" | "end" | number) {
    if (at === "start") focusAtStart(element);
    else if (at === "end") focusAtEnd(element);
    else focusAtTextOffset(element, at);
}

function focusAtEdge(element: HTMLElement, start: boolean) {
    element.focus();
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(start);
    selection.removeAllRanges();
    selection.addRange(range);
}
